import {Button} from '@wandb/components';
import {Select, SelectAsync} from '@wandb/weave/components/Form/Select';
import {ObjectViewerSection} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/CallPage/ObjectViewerSection';
import {WFDataModelAutoProvider} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/context';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import _ from 'lodash';
import React, {useMemo} from 'react';

import {type JobDetails, type JobRun} from '../JobDetail/utilTypes';
import {useConfigImporterSelection} from './hooks';
import * as S from './JobDrawerConfigImporter.styles';
import {ProjectOptionLabel} from './ProjectOptionLabel';
import {RunOptionLabel} from './RunOptionLabel';

interface JobDrawerConfigImporterProps {
  onCancel: () => void;
  onImport: () => void;
  setConfigSourceRun: (run: JobRun | null) => void;
  customRunColors?: Record<string, string>;
  configSourceRun: JobRun | null;
  defaultConfigSourceRun: JobRun | null;
  job?: JobDetails | null;
}

export const JobDrawerConfigImporter = ({
  onCancel,
  onImport,
  setConfigSourceRun,
  configSourceRun,
  customRunColors,
  defaultConfigSourceRun,
  job,
}: JobDrawerConfigImporterProps) => {
  const {
    projectOptions,
    runOptions,
    selectedProjectOption,
    selectedRunOption,
    selectedConfigSourceRun,
    loadingRuns,
    onSelectProject,
    onSelectRun,
    loadProjectOptions,
  } = useConfigImporterSelection({
    configSourceRun,
    defaultConfigSourceRun,
    job,
  });

  // the run config has some internal keys about the job that
  // we don't want to display to the user
  const cleanedConfigData = useMemo(() => {
    if (!selectedConfigSourceRun?.config) {
      return null;
    }
    return _.omitBy(selectedConfigSourceRun.config, (value, key) =>
      key.startsWith('_')
    );
  }, [selectedConfigSourceRun?.config]);

  const handleImport = () => {
    setConfigSourceRun(selectedConfigSourceRun);
    onImport();
  };

  const createProjectOptionLabel = (props: any, context: any) => (
    <ProjectOptionLabel props={props} context={context} />
  );

  const createRunOptionLabel = (props: any, context: any) => (
    <RunOptionLabel
      props={props}
      context={context}
      customRunColors={customRunColors}
    />
  );

  return (
    <S.Container>
      <S.Content>
        <S.Title>Import configuration</S.Title>
        <S.Description>
          Select a past run from one of your projects to pre-fill this job’s
          configuration. You can adjust configurations before launching.
        </S.Description>
        <S.SelectContainer>
          <S.SelectLabel>Project</S.SelectLabel>
          <SelectAsync
            cacheOptions
            defaultOptions={projectOptions}
            loadOptions={loadProjectOptions}
            formatOptionLabel={createProjectOptionLabel}
            value={selectedProjectOption}
            onChange={onSelectProject}
            placeholder="Select project"
            noOptionsMessage={({inputValue}) =>
              inputValue
                ? 'No projects found'
                : 'Type to search your team projects'
            }
          />
        </S.SelectContainer>
        <S.SelectContainer>
          <S.SelectLabel>Run</S.SelectLabel>
          {loadingRuns ? (
            <S.NoRunsMessage>Loading runs...</S.NoRunsMessage>
          ) : runOptions.length > 0 ? (
            <Select
              options={runOptions}
              formatOptionLabel={createRunOptionLabel}
              value={selectedRunOption}
              onChange={onSelectRun}
              placeholder="Select run"
            />
          ) : (
            <S.NoRunsMessage>
              This project has no past runs available.
            </S.NoRunsMessage>
          )}
        </S.SelectContainer>
        {cleanedConfigData && !loadingRuns && (
          <S.ConfigPreviewContainer>
            <WFDataModelAutoProvider>
              <S.ConfigPreviewWrapper>
                <ObjectViewerSection
                  title="Configuration Preview"
                  data={cleanedConfigData}
                />
              </S.ConfigPreviewWrapper>
            </WFDataModelAutoProvider>
          </S.ConfigPreviewContainer>
        )}
      </S.Content>
      <S.StickyFooter>
        <Tailwind>
          <div className="mx-[-50px] relative pointer-events-none border-b border-moon-250" />

          <S.ButtonContainer>
            <S.ButtonItem>
              <Button variant="secondary" fullWidth onPress={onCancel}>
                Cancel
              </Button>
            </S.ButtonItem>
            <S.ButtonItem>
              <Button
                fullWidth
                onPress={handleImport}
                isDisabled={!selectedConfigSourceRun}>
                Import
              </Button>
            </S.ButtonItem>
          </S.ButtonContainer>
        </Tailwind>
      </S.StickyFooter>
    </S.Container>
  );
};
